import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import api, { formatApiErrorDetail } from "@/lib/api";

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token") || "";
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }
    if (password !== confirm) {
      toast.error("Passwords do not match");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: password });
      toast.success("Password updated. Please sign in.");
      navigate("/admin/login");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Reset failed");
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-6 bg-background">
        <div className="w-full max-w-sm text-center">
          <h2 className="font-display font-bold text-2xl tracking-tight mb-4">
            Invalid reset link
          </h2>
          <p className="font-body text-muted-foreground mb-6">
            This link is missing or malformed. Please request a new one.
          </p>
          <Link to="/admin/forgot-password" className="font-body text-sm text-primary underline">
            Request a new reset link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-6 py-16 bg-background">
      <motion.form
        data-testid="reset-password-form"
        onSubmit={submit}
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
        className="w-full max-w-sm"
      >
        <span className="overline text-primary">Admin access</span>
        <h2 className="mt-4 font-display font-bold text-3xl tracking-tight mb-10">
          Set a new password
        </h2>

        <div className="space-y-5">
          <div>
            <Label className="font-body text-sm mb-2 block">New password</Label>
            <Input
              data-testid="reset-password-new"
              type="password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="h-12"
            />
          </div>
          <div>
            <Label className="font-body text-sm mb-2 block">Confirm password</Label>
            <Input
              data-testid="reset-password-confirm"
              type="password"
              required
              value={confirm}
              onChange={(e) => setConfirm(e.target.value)}
              className="h-12"
            />
          </div>
        </div>

        <Button
          data-testid="reset-password-submit"
          type="submit"
          disabled={loading}
          className="mt-8 w-full h-12 rounded-full bg-foreground text-background hover:bg-primary"
        >
          {loading ? "Saving..." : "Set new password"}
        </Button>
      </motion.form>
    </div>
  );
}
